import {
  GeoJSONSource,
  Map as MapLibreMap,
  setWorkerUrl,
  type LngLatBoundsLike,
  type MapMouseEvent,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
// MapLibre 6 derives its worker URL at runtime (`new URL('./' + name, import.meta.url)`), which
// the bundler can't see, so the worker file never gets emitted. Let Vite bundle the worker (with
// its shared chunk) and hand MapLibre the resulting URL.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { useEffect, useRef } from 'preact/hooks';
import { maxX as gridMaxX, minY as gridMinY } from '../sim/grid.ts';
import type { AreaSelection } from '../sim/resolution.ts';
import { area, gridPlan, setAreaCenter } from '../state/store.ts';
import { wgs84ToUtm33 } from '../terrain/projections.ts';
import { densifiedUtmRectangle } from './utmRectangle.ts';

setWorkerUrl(maplibreWorkerUrl);

const KARTVERKET_TILES =
  'https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png';
const NORWAY_CENTER: [number, number] = [10, 64.5];
const NORWAY_ZOOM = 5;

function emptyFeatureCollection(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

function rectangleFeature(minX: number, minY: number, maxX: number, maxY: number): GeoJSON.Feature {
  const ring = densifiedUtmRectangle(minX, minY, maxX, maxY);
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [ring] },
  };
}

/** Lng/lat bounds of a selection with a margin of `margin`× its size on each side. */
function selectionBounds(a: AreaSelection, margin = 0.4): LngLatBoundsLike {
  const hw = (a.width / 2) * (1 + 2 * margin);
  const hh = (a.height / 2) * (1 + 2 * margin);
  const ring = densifiedUtmRectangle(
    a.centerX - hw,
    a.centerY - hh,
    a.centerX + hw,
    a.centerY + hh,
    4,
  );
  const lngs = ring.map((p) => p[0]);
  const lats = ring.map((p) => p[1]);
  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
}

/** MapLibre area picker over Kartverket's topo raster tiles. Clicking sets the selection centre;
 * dragging the drawn rectangle moves it. The rectangle itself always reflects `gridPlan`'s
 * (rounded-up) terrain extent when available, so the user sees the real area that will be
 * fetched. */
export function MapPicker() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const initialArea = area.value;

    const map = new MapLibreMap({
      container,
      ...(initialArea
        ? { bounds: selectionBounds(initialArea) }
        : { center: NORWAY_CENTER, zoom: NORWAY_ZOOM }),
      attributionControl: { customAttribution: 'Terrain & maps © Kartverket' },
      style: {
        version: 8,
        sources: {
          kartverket: {
            type: 'raster',
            tiles: [KARTVERKET_TILES],
            tileSize: 256,
            maxzoom: 18,
            attribution: '© Kartverket',
          },
        },
        layers: [{ id: 'kartverket', type: 'raster', source: 'kartverket' }],
      },
    });
    mapRef.current = map;

    let dragging = false;

    map.on('load', () => {
      map.addSource('selection', { type: 'geojson', data: emptyFeatureCollection() });
      map.addLayer({
        id: 'selection-fill',
        type: 'fill',
        source: 'selection',
        paint: { 'fill-color': '#ff8c00', 'fill-opacity': 0.15 },
      });
      map.addLayer({
        id: 'selection-line',
        type: 'line',
        source: 'selection',
        paint: { 'line-color': '#ff8c00', 'line-width': 2 },
      });
      syncRectangle();

      map.on('mouseenter', 'selection-fill', () => {
        if (!dragging) map.getCanvas().style.cursor = 'grab';
      });
      map.on('mouseleave', 'selection-fill', () => {
        if (!dragging) map.getCanvas().style.cursor = '';
      });

      map.on('mousedown', 'selection-fill', (e) => {
        const current = area.value;
        if (!current) return;
        e.preventDefault();
        dragging = true;
        map.dragPan.disable();
        map.getCanvas().style.cursor = 'grabbing';
        const [startX, startY] = wgs84ToUtm33(e.lngLat.lng, e.lngLat.lat);

        const onMove = (ev: MapMouseEvent): void => {
          const [x, y] = wgs84ToUtm33(ev.lngLat.lng, ev.lngLat.lat);
          const cx = current.centerX + (x - startX);
          const cy = current.centerY + (y - startY);
          const src = map.getSource('selection') as GeoJSONSource | undefined;
          src?.setData(
            rectangleFeature(
              cx - current.width / 2,
              cy - current.height / 2,
              cx + current.width / 2,
              cy + current.height / 2,
            ),
          );
        };
        const onUp = (ev: MapMouseEvent): void => {
          map.off('mousemove', onMove);
          map.off('mouseup', onUp);
          dragging = false;
          map.dragPan.enable();
          map.getCanvas().style.cursor = 'grab';
          const [x, y] = wgs84ToUtm33(ev.lngLat.lng, ev.lngLat.lat);
          setAreaCenter(current.centerX + (x - startX), current.centerY + (y - startY));
        };
        map.on('mousemove', onMove);
        map.on('mouseup', onUp);
      });

      map.on('click', (e) => {
        if (dragging) return;
        const hits = map.queryRenderedFeatures(e.point, { layers: ['selection-fill'] });
        if (hits.length > 0) return;
        const [x, y] = wgs84ToUtm33(e.lngLat.lng, e.lngLat.lat);
        setAreaCenter(x, y);
      });
    });

    function syncRectangle(): void {
      const src = map.getSource('selection') as GeoJSONSource | undefined;
      if (!src) return;
      const plan = gridPlan.value;
      const a = area.value;
      if (plan) {
        const spec = plan.terrain;
        src.setData(rectangleFeature(spec.minX, gridMinY(spec), gridMaxX(spec), spec.maxY));
      } else if (a) {
        src.setData(
          rectangleFeature(
            a.centerX - a.width / 2,
            a.centerY - a.height / 2,
            a.centerX + a.width / 2,
            a.centerY + a.height / 2,
          ),
        );
      } else {
        src.setData(emptyFeatureCollection());
      }
    }

    const unsubscribe = gridPlan.subscribe(() => syncRectangle());

    return () => {
      unsubscribe();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  return <div class="map-picker" ref={containerRef} />;
}
